import { SxProps } from '@mui/material';
import { NotePencil } from '@phosphor-icons/react';
import { useMemo, useState } from 'react';
import { UseFormReset } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { MenuButton } from 'core/elements';
import { QuoteEntityType, QuoteType } from 'quotes/enums';
import { useCustomerQuotePartsContext } from 'quotes/states';
import { IQuotePart, IQuotePartNoteForm } from 'quotes/types';
import { Color, Variant } from 'theme/enums';

import { QuotePartAddNote } from './QuotePartAddNote';

interface IProps {
  quoteParts?: IQuotePart[];
  hasUndiscardableNote?: boolean;
  level?: number;
  onSubmit?: (reset?: UseFormReset<IQuotePartNoteForm>) => (data: IQuotePartNoteForm) => void;
  onSubmitSuccess?: () => void;
  disabled?: boolean;
  entityType?: QuoteEntityType;
  plainButton?: boolean;
  startDecorator?: JSX.Element;
  sx?: SxProps;
  isAlternateVersionEnabled?: boolean;
  quoteType?: QuoteType;
}

export const QuotePartNoteAddButton = ({
  quoteParts,
  hasUndiscardableNote = undefined,
  level = 0,
  onSubmit = null,
  onSubmitSuccess = null,
  disabled = false,
  entityType,
  plainButton = false,
  startDecorator = <NotePencil />,
  sx,
  isAlternateVersionEnabled = false,
  quoteType,
}: IProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const quotePartsArray = useMemo(() => quoteParts || [], [quoteParts]);

  const { t } = useTranslation();

  const { setDraftVersionChanged } = useCustomerQuotePartsContext();

  const handleOnSubmitSuccess = () => {
    onSubmitSuccess && onSubmitSuccess();

    setDraftVersionChanged();
  };

  const handleOnClose = () => setIsOpen(false);

  return (
    <>
      <MenuButton
        label={t('buttonLabelAddNotes')}
        color={Color.PRIMARY}
        variant={plainButton ? Variant.PLAIN : Variant.SOLID}
        startDecorator={!plainButton && startDecorator}
        endDecorator={null}
        onClick={() => setIsOpen(true)}
        disabled={disabled}
        sx={sx}
      />

      <QuotePartAddNote
        isOpen={isOpen}
        level={level}
        onClose={handleOnClose}
        quoteParts={quotePartsArray}
        hasUndiscardableNote={hasUndiscardableNote}
        onSubmit={onSubmit}
        onSubmitSuccess={handleOnSubmitSuccess}
        entityType={entityType}
        isAlternateVersionEnabled={isAlternateVersionEnabled}
        quoteType={quoteType}
      />
    </>
  );
};
